import classNames from 'classnames'
import React, { forwardRef, HTMLAttributes } from 'react'
import { Icon } from '../elements'

interface LabelProps extends HTMLAttributes<HTMLLabelElement> {
    text?: string
    /** ID of the form element this is used for */
    htmlFor?: string
    required?: boolean
    optional?: boolean
    hint?: string
    hintPosition?: 'left' | 'right' | 'top' | 'bottom'
    className?: string
    bold?: boolean
    children?: React.ReactNode
    hintIconClassName?: string
    hintIconColor?: string
}

const Label = forwardRef<HTMLLabelElement, LabelProps>(({
    text,
    htmlFor,
    required,
    optional,
    hint,
    hintPosition = 'right',
    bold,
    className,
    children,
    hintIconClassName = 'ms-1',
    hintIconColor = 'primary',
    ...otherProps
}, ref) => {

    // Function to render the hint icon when hint text is provided
    const renderHint = () => {
        if (!hint) return null

        return (
            <button
                id={`${htmlFor}-hint`}
                className={classNames(hintIconClassName, 'btn btn-link')}
                data-tooltip={hint}
                data-tooltip-pos={hintPosition}
                role="tooltip"
                type="button"
            >
                <Icon
                    name="circle-info"
                    type="regular"
                    color={hintIconColor}
                    aria-hidden="true"
                />
            </button>
        )
    }

    return (
        <label
            ref={ref}
            htmlFor={htmlFor}
            className={classNames({
                'fw-bold': bold,
                'cursor-pointer': htmlFor
            }, className)}
            {...otherProps}
        >
            {text}
            {required && (
                <Icon className="ms-1" size="xs" name="asterisk" color="danger" faProps={{title: 'Required'}}/>
            )}
            {optional && <small className="ms-1 text-muted"> (optional)</small>}
            {hint && (
                <>
                    <span aria-hidden="true">
                        {renderHint()}
                    </span>
                    <span className="visually-hidden">Tip: {hint}</span>
                </>
            )}
            {children}
        </label>
    )
})

export { Label }
